import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { QUESTIONNAIRE_SECTIONS as STATIC_SECTIONS } from './data/questionnaire';
import Sidebar from './components/Sidebar';
import StepRenderer from './components/StepRenderer';
import SafeIcon from './common/SafeIcon';
import Auth from './components/Auth';
import AdminDashboard from './components/AdminDashboard';
import ThankYou from './components/ThankYou';
import SubmissionHistory from './components/SubmissionHistory';
import { supabase } from './supabase/supabase';

function App() {
  const [session, setSession] = useState(null);
  const [isAdminView, setIsAdminView] = useState(false);
  const [activeView, setActiveView] = useState('form'); // 'form', 'history'
  const [showAuth, setShowAuth] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [sections, setSections] = useState(STATIC_SECTIONS);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('growth_form_draft');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadSchema = useCallback(async () => {
    try {
      const { data } = await supabase.from('form_schema_2024').select('schema_data').maybeSingle();
      if (data?.schema_data?.length > 0) setSections(data.schema_data);
    } catch (err) {
      setSections(STATIC_SECTIONS);
    }
  }, []);

  const syncToSupabase = useCallback(async (updatedData, step, completed, activeSession) => {
    const user = activeSession?.user || session?.user;
    if (user?.email === 'accounts@punkmypixel.com') return;

    try {
      const targetEmail = updatedData.contactEmail || user?.email || 'guest@anonymous.com';
      const payload = {
        user_id: user?.id || null,
        email: targetEmail,
        form_data: updatedData,
        current_step: step,
        is_completed: completed,
        updated_at: new Date().toISOString(),
      };

      let query;
      if (user?.id) {
        query = supabase.from('growth_forms_2024').upsert(payload, { onConflict: 'user_id' });
      } else {
        if (completed || updatedData.contactEmail) {
          query = supabase.from('growth_forms_2024').insert(payload);
        } else {
          return;
        }
      }

      const { error } = await query;
      if (!error) {
        setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      }
    } catch (err) {
      console.error("Sync error:", err);
    }
  }, [session]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadSchema();
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
      if (currentSession?.user?.email === 'accounts@punkmypixel.com') setIsAdminView(true);
      setLoading(false);
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.user?.email === 'accounts@punkmypixel.com') setIsAdminView(true);
    });
    return () => subscription?.unsubscribe();
  }, [loadSchema]);

  useEffect(() => {
    if (!session) {
      localStorage.setItem('growth_form_draft', JSON.stringify(formData));
    }
  }, [formData, session]);

  const handleNext = async () => {
    const isLastStep = currentStep === sections.length - 1;
    if (isLastStep) {
      setSubmitting(true);
      await syncToSupabase(formData, currentStep, true, session);
      setIsCompleted(true);
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (session || formData.contactEmail) {
        syncToSupabase(formData, nextStep, false, session);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleResumeSubmission = (submission) => {
    setFormData(submission.form_data || {});
    setCurrentStep(submission.current_step || 0);
    setIsCompleted(submission.is_completed || false);
    setActiveView('form');
  };

  if (loading) return (
    <div className="h-screen w-full bg-slate-900 flex flex-col items-center justify-center gap-4">
      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (isAdminView) return <AdminDashboard onBack={() => setIsAdminView(false)} />;
  if (showAuth) return <Auth onBack={() => setShowAuth(false)} />;
  if (isCompleted && activeView === 'form') return <ThankYou onBack={() => setIsCompleted(false)} />;

  return (
    <div className="flex h-screen bg-white overflow-hidden relative">
      <Sidebar 
        sections={sections} 
        currentStep={currentStep} 
        onStepClick={(idx) => {
          setActiveView('form');
          setCurrentStep(idx);
        }}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        formData={formData}
        session={session}
        onSaveProgress={() => setShowAuth(true)}
        onViewHistory={() => setActiveView('history')}
        activeView={activeView}
      />
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50/50 relative">
        <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 text-slate-600">
              <SafeIcon name="Menu" className="w-6 h-6" />
            </button>
            <div className="hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
              <div className={`w-2 h-2 rounded-full ${lastSaved ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
              {lastSaved ? `Synced: ${lastSaved}` : 'Ready to Sync'}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!session && <button onClick={() => setShowAuth(true)} className="text-xs font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-6 py-2.5 rounded-2xl hover:bg-blue-100">Sign In</button>}
            {session && <button onClick={() => supabase.auth.signOut()} className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900">Logout</button>}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-12 md:px-12">
          <div className="max-w-4xl mx-auto">
            {activeView === 'history' ? (
              <SubmissionHistory 
                session={session} 
                onBack={() => setActiveView('form')} 
                onResume={handleResumeSubmission}
              />
            ) : (
              <div className="bg-white rounded-[48px] shadow-sm border border-slate-200 p-8 md:p-16 mb-40">
                <AnimatePresence mode="wait">
                  <StepRenderer 
                    key={currentStep}
                    section={sections[currentStep]}
                    formData={formData}
                    handleInputChange={(e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                  />
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {activeView === 'form' && (
          <div className="absolute bottom-0 left-0 w-full bg-white/80 backdrop-blur-xl border-t border-slate-200 px-6 py-6 z-10">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <button 
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="px-10 py-4 rounded-[22px] font-black uppercase tracking-widest text-[11px] text-slate-400 border-2 border-slate-100 disabled:opacity-20 hover:bg-slate-50"
              >
                Back
              </button>
              <button 
                onClick={handleNext}
                disabled={submitting}
                className="px-12 py-4 rounded-[22px] font-black uppercase tracking-widest text-[11px] text-white bg-blue-600 shadow-2xl shadow-blue-200 hover:bg-blue-700 active:scale-95 transition-all"
              >
                {submitting ? 'Finalizing...' : (currentStep === sections.length - 1 ? 'Submit & Finalize' : 'Save & Continue')}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;